<template>
    <PersistentGroupingToggle
        v-if="showGrouping && groupingSaveKey && (!sortSaveKey || !sortOrderEntries?.length || selectedSortEntry)"
        class="q-mx-md"
        :grouping-save-key="groupingSaveKey"
    />
    <PersistentSortDropdown
        v-if="sortSaveKey && sortOrderEntries"
        :sort-order-entries="sortOrderEntries"
        :sort-save-key="sortSaveKey"
        :initial-selected-sort-entry="initialSelectedSortEntry"
        v-model:selected-sort-entry="selectedSortEntry"
    />
    <div v-if="moreMenuEntries" data-qs="list-frame-with-toolbar-three-dot-menu">
        <div class="row items-center more-menu-container">
            <MoreMenu :entries="moreMenuEntries" />
        </div>
    </div>
</template>

<script setup lang="ts">
import MoreMenu from "@/shared/components/menu/more-menu.vue";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import { computed, ref, toRefs, watch } from "vue";
import PersistentSortDropdown from "@/shared/components/sort-dropdown/persistent-sort-dropdown.vue";
import PersistentGroupingToggle from "@/shared/list-filter/persistent-grouping-toggle.vue";
import { watchImmediate } from "@vueuse/core";
import type { ListActionsProps } from "@/shared/list-filter/list-filter.types";
import { ContentSortFields, SortOrder } from "@/shared/services/graphql/generated/consumer-graph-types.ts";
import { useGroupingStore } from "@/shared/grouping/store/grouping.store.ts";

const props = defineProps<ListActionsProps>();

const emits = defineEmits<{
    onSortSelectionChanged: [sortEntry: SortEntry];
}>();

const { moreMenuEntries } = toRefs(props);
const selectedSortEntry = ref<SortEntry>();

const groupingStore = useGroupingStore();

const isGroupingActive = computed(
    () => (props.groupingSaveKey ? groupingStore.isGrouped[props.groupingSaveKey] : false) ?? false
);

watchImmediate(selectedSortEntry, () => {
    if (!selectedSortEntry.value) return;
    emits("onSortSelectionChanged", selectedSortEntry.value);
});

const versionDesc: SortEntry = { field: ContentSortFields.version, order: SortOrder.desc };

const isSortAvailable = (sort: SortEntry) =>
    props.sortOrderEntries?.some((e) => e.value.field === sort.field && e.value.order === sort.order) ?? false;

// While grouping is active the sort options shrink (no Title/Ranking). The sort can be resolved before the
// grouping is known (reload) or before the parent updated the options, so this reacts to all three.
watch([isGroupingActive, selectedSortEntry, () => props.sortOrderEntries], () => {
    const selected = selectedSortEntry.value;
    if (!isGroupingActive.value || !selected || isSortAvailable(selected)) return;

    if (isSortAvailable(versionDesc)) selectedSortEntry.value = versionDesc;
});
</script>
